import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Car, MagnifyingGlass, Trash, Clock, Robot } from '@phosphor-icons/react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import Pagination, { usePagination } from '../../components/Pagination';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProStock = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/api/pro/vehicles`).then(r => { setVehicles(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const deleteVehicle = async (id) => {
    if (!window.confirm('Supprimer ce véhicule ?')) return;
    await axios.delete(`${API_URL}/api/pro/vehicles/${id}`);
    setVehicles(prev => prev.filter(v => v.id !== id));
  };

  // Helper: effective brand = declared OR Gemini-identified
  const getEffBrand = (v) => (v.brand || v.vehicle_identified?.brand || '').toUpperCase().trim();
  const getEffModel = (v) => (v.model || v.vehicle_identified?.model || '');
  const getEffYear = (v) => (v.year || v.vehicle_identified?.year || '');

  // Build the brand dropdown options from the actual fleet
  const allBrands = Array.from(new Set(
    vehicles.map(v => getEffBrand(v)).filter(Boolean)
  )).sort();

  const filtered = vehicles.filter(v => {
    if (filter !== 'all' && v.status !== filter) return false;
    if (brandFilter && !getEffBrand(v).includes(brandFilter.toUpperCase())) return false;
    if (dateFrom) {
      const t = new Date(v.created_at).getTime();
      if (isNaN(t) || t < new Date(dateFrom).getTime()) return false;
    }
    if (dateTo) {
      const t = new Date(v.created_at).getTime();
      const end = new Date(dateTo).getTime() + 24 * 60 * 60 * 1000;
      if (isNaN(t) || t >= end) return false;
    }
    if (search) {
      const s = search.toLowerCase();
      return getEffBrand(v).toLowerCase().includes(s)
        || getEffModel(v).toLowerCase().includes(s)
        || (v.vin || '').toLowerCase().includes(s)
        || (v.registration || '').toLowerCase().includes(s);
    }
    return true;
  });

  const resetFilters = () => { setFilter('all'); setBrandFilter(''); setDateFrom(''); setDateTo(''); setSearch(''); setCurrentPage(1); };

  const { currentPage, setCurrentPage, paginated, totalPages, totalItems, rangeStart, rangeEnd } = usePagination(filtered, 20);

  const statusStyles = {
    received: 'bg-amber-100 text-amber-700',
    analyzing: 'bg-blue-100 text-blue-700',
    cataloged: 'bg-green-100 text-green-700',
    active: 'bg-[#2A9D8F]/10 text-[#2A9D8F]',
    error: 'bg-red-100 text-red-700',
    archived: 'bg-gray-100 text-gray-500',
  };
  const statusLabels = { received: 'Reçu', analyzing: 'Analyse...', cataloged: 'Catalogué', active: 'En vente', error: 'Erreur', archived: 'Archivé' };

  if (loading) return <AdminLayout title="Stock Véhicules"><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#E63946] border-t-transparent rounded-full animate-spin" /></div></AdminLayout>;

  return (
    <AdminLayout title="Stock Véhicules">
      <div className="bg-white border border-gray-200 p-4 mb-4 grid grid-cols-1 md:grid-cols-6 gap-3" data-testid="vehicle-filters">
        <div className="relative md:col-span-2">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text" value={search} onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
            placeholder="Marque, modèle, VIN, plaque..."
            className="w-full pl-10 pr-3 py-2 border border-gray-200 text-sm focus:border-[#E63946] outline-none"
            data-testid="stock-search"
          />
        </div>
        <select value={filter} onChange={e => { setFilter(e.target.value); setCurrentPage(1); }} className="py-2 px-3 border border-gray-200 text-sm" data-testid="stock-filter">
          <option value="all">Tous statuts</option>
          <option value="received">Reçus</option>
          <option value="analyzing">Analyse...</option>
          <option value="cataloged">Catalogués</option>
          <option value="active">En vente</option>
          <option value="archived">Archivés</option>
        </select>
        <select value={brandFilter} onChange={e => { setBrandFilter(e.target.value); setCurrentPage(1); }} className="py-2 px-3 border border-gray-200 text-sm" data-testid="filter-brand">
          <option value="">Toutes marques</option>
          {allBrands.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        <div className="flex flex-col">
          <label className="text-gray-400 text-[10px] uppercase mb-0.5">Du</label>
          <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setCurrentPage(1); }} className="border border-gray-200 px-2 py-1 text-sm" data-testid="filter-date-from" />
        </div>
        <div className="flex flex-col">
          <label className="text-gray-400 text-[10px] uppercase mb-0.5">Au</label>
          <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setCurrentPage(1); }} className="border border-gray-200 px-2 py-1 text-sm" data-testid="filter-date-to" />
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <span className="text-gray-500 text-xs" data-testid="vehicle-count">{filtered.length} véhicule{filtered.length > 1 ? 's' : ''} {filtered.length !== vehicles.length && `(sur ${vehicles.length})`}</span>
        <div className="flex gap-2">
          <button onClick={resetFilters} className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 text-xs font-medium border border-gray-200" data-testid="filter-reset">Réinitialiser</button>
          <Link to="/admin/pro/reception" className="bg-[#E63946] text-white px-4 py-1.5 text-xs font-bold uppercase tracking-wide hover:bg-[#D90429]" data-testid="add-vehicle-btn">+ Nouveau véhicule</Link>
        </div>
      </div>

      <div className="bg-white border border-gray-200 shadow-sm">
        <div className="divide-y divide-gray-100">
          {paginated.length > 0 ? paginated.map((v) => {
            const showGeminiBadge = !v.brand && v.vehicle_identified?.brand;
            return (
              <Link key={v.id} to={`/admin/pro/vehicle/${v.id}`} className="block p-4 md:p-5 hover:bg-gray-50" data-testid={`vehicle-row-${v.id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <Car size={20} className="text-gray-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-gray-900 font-semibold text-sm md:text-base">
                          {getEffBrand(v) || 'Véhicule'} {getEffModel(v)} {getEffYear(v)}
                          {showGeminiBadge && (
                            <span className="ml-2 inline-flex items-center gap-1 bg-[#2A9D8F]/10 text-[#2A9D8F] text-[10px] px-1.5 py-0.5 font-bold uppercase">
                              <Robot size={10} weight="bold" />Gemini
                            </span>
                          )}
                        </p>
                      </div>
                      <span className={`px-2 py-0.5 text-[10px] md:text-xs font-bold uppercase flex-shrink-0 ${statusStyles[v.status] || statusStyles.received}`}>
                        {statusLabels[v.status] || v.status}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 md:gap-4 mt-1 text-xs text-gray-400">
                      {v.registration && <span className="font-mono uppercase">{v.registration}</span>}
                      <span className="flex items-center gap-1"><Clock size={12} />{new Date(v.created_at).toLocaleDateString('fr-FR')}</span>
                      <span className="text-[#2A9D8F] font-bold">{(v.total_estimated_value || 0).toFixed(0)} &euro;</span>
                      <span>{(v.parts_inventory || []).length} pi&egrave;ces</span>
                    </div>
                  </div>
                  <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); deleteVehicle(v.id); }} className="p-2 text-gray-300 hover:text-[#E63946] flex-shrink-0" data-testid={`delete-vehicle-${v.id}`}><Trash size={16} /></button>
                </div>
              </Link>
            );
          }) : <div className="p-12 text-center text-gray-400">Aucun véhicule trouvé</div>}
        </div>
        <div className="px-4 pb-2">
          <Pagination currentPage={currentPage} totalPages={totalPages} onChange={setCurrentPage} rangeStart={rangeStart} rangeEnd={rangeEnd} totalItems={totalItems} />
        </div>
      </div>
    </AdminLayout>
  );
};

export default ProStock;
